/**
 * Route copy for `src/routes/projects` (keys: `routes.projects.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  acceptInvitePage: {
    invalidInvitationLink: 'Invalid invitation link.',
    invitationAcceptedRedirectingToTheProjec: 'Invitation accepted — redirecting to the project…',
    thisInvitationCannotBeFound: 'This invitation cannot be found.',
    thisInvitationHasExpiredOrHas: 'This invitation has expired or has already been used.',
    youAreAlreadyAMemberOf: 'You are already a member of this project.',
    couldNotAcceptTheInvitationError: 'Could not accept the invitation (error {{status}}).',
    couldNotAcceptTheInvitationRight: 'Could not accept the invitation right now.',
    validatingYourInvitation: 'Validating your invitation…',
    invalidInvitation: 'Invalid invitation',
    viewMyProjects: 'View my projects',
  },
  confirmDialog: {
    confirm: 'Confirm',
    cancel: 'Cancel',
  },
  detailChecklistTab: {
    couldNotUpdate: 'Could not update.',
    couldNotDelete: 'Could not delete.',
    markTitleAsDone: 'Mark “{{title}}” as done',
    ai: 'AI',
    taskStatus: 'Task status',
    assignedTo: 'Assigned to',
    unassigned: 'Unassigned',
    timeline: 'Timeline',
    dueV: 'Due: {{v}}',
    deleteTitle: 'Delete “{{title}}”',
    deleteTheTask: 'Delete the task',
    addATask: 'Add a task…',
    add: 'Add',
    couldNotAddTheTask: 'Could not add the task.',
    addATask2: 'Add a task',
    stage: 'Stage',
    stageOfTheFirstTask: 'Stage of the first task',
    task: 'Task',
    cancel: 'Cancel',
    noTaskYet: 'No task yet.',
    generateTheAiLaunchPlanFrom: 'Generate the AI launch plan from “Overview”, or add a first task below.',
    addATask3: '+ Add a task',
    noTaskAtThisStage: 'No task at this stage.',
  },
  detailLeadsTab: {
    leadCountOne: '{{count}} lead',
    leadCountMany: '{{count}} leads',
    couldNotAttachTheGroup: 'Could not attach the group.',
    couldNotCreateAttach: 'Could not create / attach.',
    attachALeadGroup: 'Attach a lead group',
    existingGroup: 'Existing group',
    crmWriteAccessRequiredToCreate: 'CRM write access required to create a group',
    newGroup: 'New group',
    searchForAGroup: 'Search for a group…',
    searchForALeadGroup: 'Search for a lead group',
    noCrmGroupCreateOneIn: 'No CRM group. Create one in the “New group” tab.',
    noGroupAvailableAllAlreadyAttached: 'No group available (all already attached).',
    attach: 'Attach',
    emoji: 'Emoji',
    name: 'Name',
    eGTradeShowLeads2026: 'e.g. Trade show leads 2026',
    description: 'Description',
    optional: '(optional)',
    color: 'Color',
    colorC: 'Color {{c}}',
    cancel: 'Cancel',
    creating: 'Creating…',
    createAndAttach: 'Create and attach',
    groupNotFoundInTheCrm: 'Group not found in the CRM (maybe deleted).',
    hideTheLeads: 'Hide the leads',
    workTheLeads: 'Work the leads',
    manageInTheCrm: 'Manage in the CRM →',
    detach: 'Detach',
    loadingLeads: 'Loading leads…',
    noLeadInThisGroup: 'No lead in this group.',
    addLeads: 'Add leads',
    leadScore: 'Lead score',
    detachTheGroup: 'Detach the group',
    detachThisLeadGroupFromThe: 'Detach this lead group from the project? The group and its contacts stay in the CRM.',
    couldNotLoadTheLeadsOf: 'Could not load the leads of one or more groups.',
    couldNotDetachTheGroup: 'Could not detach the group.',
    projectLeadGroups: 'Project lead groups',
    organizeAndWorkThisProjectS: 'Organize and work this project\'s leads, group by group.',
    attachALeadGroup2: '+ Attach a lead group',
    noLeadGroupAttached: 'No lead group attached.',
    attachACrmGroupToWork: 'Attach a CRM group to work its leads in the context of this project.',
  },
  detailOverviewTab: {
    noTasksThePlanHasNot: 'No tasks — the plan has not been generated yet.',
    allTotalTasksAreDoneWell: 'All {{total}} tasks are done. Well done!',
    doneTaskSDoneOutOf: '{{done}} task(s) done out of {{total}}.',
    aiPlanGeneratedLengthStages: 'AI plan generated · {{length}} stages',
    noAiPlanGenerateItBelow: 'No AI plan — generate it below',
    donepctOfTheChecklistDone: '{{donePct}}% of the checklist done',
    overdueOverdueTaskS: '{{overdue}} overdue task(s)',
    inprogressTaskSInProgress: '{{inProgress}} task(s) in progress',
    unassignedUnassignedTaskS: '{{unassigned}} unassigned task(s)',
    membersactiveActiveMemberS: '{{membersActive}} active member(s)',
    resourcestotalLinkedResourceS: '{{resourcesTotal}} linked resource(s)',
    viaAi: 'via AI',
    defaultPlan: 'default plan',
    launchPlanGeneratedViaLengthTasks: 'Launch plan generated ({{via}}) — {{length}} tasks created.',
    planGenerationFailed: 'Plan generation failed.',
    checklistProgress: 'Checklist progress',
    checklistdoneChecklisttotalTasksDone: '{{checklistDone}} / {{checklistTotal}} tasks done',
    currentStage: 'Current stage',
    activeMembers: 'Active members',
    linkedResources: 'Linked resources',
    completed: 'completed',
    launchPlanProgress: 'Launch plan progress',
    tasksCompletedPerStageStrategyGrowth: 'Tasks completed per stage (strategy → growth)',
    serverDashboardUnavailableIndicatorsComp: 'Server dashboard unavailable — indicators computed locally.',
    unavailableSources: 'Unavailable sources:',
    checklistPerStage: 'Checklist per stage',
    noTasksYetGenerateTheAi: 'No tasks yet — generate the AI plan to structure it.',
    keyPoints: 'Key points',
    aiLaunchPlan: 'AI launch plan',
    planGeneratedVLengthStages: 'Plan generated ({{v}}) — {{length}} stages.',
    generateAStructuredPlanStrategyGrowth: 'Generate a structured plan (strategy → growth) and its checklist.',
    editorRoleMinimumRequired: 'Editor role minimum required',
    generating: 'Generating…',
    regenerateTheAiPlan: '↻ Regenerate the AI plan',
    generateTheAiPlan: '✨ Generate the AI plan',
    noPlanYetRunTheGeneration: 'No plan yet — run the generation to structure the project.',
    ai: 'AI',
    default: 'default',
  },
  detailProgressRing: {
    clampedDone: '{{clamped}}% done',
  },
  detailResourcesTab: {
    couldNotDetachTheResource: 'Could not detach the resource.',
    open: 'Open →',
    detach: 'Detach',
    detachTheResource: 'Detach the resource',
    detachThisResourceFromTheProject: 'Detach this resource from the project?',
    couldNotAttachTheResource: 'Could not attach the resource.',
    attachAResource: 'Attach a resource',
    type: 'Type',
    resourceIdentifier: 'Resource identifier',
    eGPlan123: 'e.g. plan-123',
    label: 'Label',
    optional: '(optional)',
    displayName: 'Display name',
    cancel: 'Cancel',
    adding: 'Adding…',
    attach: 'Attach',
    resources: 'Resources',
    types: 'Types',
    attachAResource2: '+ Attach a resource',
    noLinkedResource: 'No linked resource.',
    linkContentPlansCampaignsCrmGroups: 'Link content plans, campaigns, CRM groups… to this project.',
  },
  detailTeamTab: {
    couldNotCreateTheInvitation: 'Could not create the invitation.',
    invitationCreated: 'Invitation created',
    shareThisLinkWith: 'Share this link with',
    vTheyWillJoinTheProject: '({{v}}). They will join the project by opening it.',
    noEmailIsSentAutomaticallyCopy: 'No email is sent automatically: copy this link and pass it on yourself (email, messaging…) so the person joins the project.',
    copied: 'Copied ✓',
    copy: 'Copy',
    inviteAMember: 'Invite a member',
    emailAddress: 'Email address',
    role: 'Role',
    cancel: 'Cancel',
    sending: 'Sending…',
    createInvitation: 'Create invitation',
    active: 'Active',
    guest: 'Guest',
    withdrawn: 'Withdrawn',
    couldNotChangeTheRole: 'Could not change the role.',
    couldNotRemoveThisMember: 'Could not remove this member.',
    you: '(you)',
    memberRole: 'Member role',
    remove: 'Remove',
    removeMember: 'Remove member',
    removeDisplaynameFromTheProject: 'Remove {{displayName}} from the project?',
    revokeTheInvitationOfEmailThe: 'Revoke the invitation of “{{email}}”? The link will no longer be valid.',
    couldNotRevokeTheInvitation: 'Could not revoke the invitation.',
    members: 'Members',
    adminRoleMinimumRequired: 'Admin role minimum required',
    invite: '+ Invite',
    noMembers: 'No members.',
    inviteCollaboratorsToWorkOnThis: 'Invite collaborators to work on this project together.',
    pendingInvitations: 'Pending invitations',
    expiresOnV: 'Expires on {{v}}',
    pending: 'Pending',
    revoke: 'Revoke',
    colleagueExampleCom: 'colleague@example.com',
  },
  format: {
    strategy: 'Strategy',
    branding: 'Branding',
    content: 'Content',
    influencers: 'Influencers',
    launch: 'Launch',
    growth: 'Growth',
    active: 'Active',
    paused: 'Paused',
    archived: 'Archived',
    viewer: 'Viewer',
    editor: 'Editor',
    admin: 'Admin',
    owner: 'Owner',
    toDo: 'To do',
    inProgress: 'In progress',
    done: 'Done',
    skipped: 'Skipped',
    contentPlan: 'Content plan',
    emailCampaign: 'Email campaign',
    emailSequence: 'Email sequence',
    crmGroup: 'CRM group',
    influencerList: 'Influencer list',
    hashtagMonitoring: 'Hashtag monitoring',
    activationSchedule: 'Activation schedule',
    designDocument: 'Design document',
  },
  projectDetailPage: {
    overview: 'Overview',
    checklist: 'Checklist',
    leads: 'Leads',
    resources: 'Resources',
    team: 'Team',
    team2: 'team',
    dashboard: 'dashboard',
    couldNotLoadThisProjectOr: 'Could not load this project, or access denied.',
    stageAdvancedV: 'Stage advanced: {{v}}.',
    couldNotAdvanceTheStage: 'Could not advance the stage.',
    loadingTheProject: 'Loading the project',
    loading: 'Loading…',
    projectUnavailable: 'Project unavailable',
    projectNotFound: 'Project not found.',
    backToProjects: 'Back to projects',
    projects: '← Projects',
    someSectionsCouldNotBeLoaded: 'Some sections could not be loaded (',
    theirContentMayAppearEmpty: ') — their content may appear empty.',
    topUp: 'Top up',
    adminRoleMinimumRequired: 'Admin role minimum required',
    alreadyAtTheLastStage: 'Already at the last stage',
    advancing: 'Advancing…',
    lastStageReached: 'Last stage reached',
    advanceTheStage: 'Advance the stage →',
    yourRoleCurrentrole: 'Your role: {{currentRole}}',
    stage: 'Stage',
    members: 'Members',
  },
  projectsListPage: {
    ai: '✨ AI',
    aiPlan: 'AI plan',
    stageV: 'Stage: {{v}}',
    deletePermanentlyCannotBeUndone: 'Delete permanently (cannot be undone)',
    delete: 'Delete',
    couldNotCreateTheProject: 'Could not create the project.',
    planGenerationFailedYouCanTry: 'Plan generation failed. You can try again.',
    projectCreated: 'Project created',
    hasBeenCreatedWouldYouLike: 'has been created. Would you like to generate an AI launch plan (strategy → growth) and its checklist now?',
    later: 'Later',
    generating: 'Generating…',
    generateTheAiLaunchPlan: '✨ Generate the AI launch plan',
    newProject: 'New project',
    projectName: 'Project name',
    launchOfTheAcmeBrand: 'Launch of the Acme brand',
    objective: 'Objective',
    reach10000FollowersIn3: 'Reach 10,000 followers in 3 months',
    description: 'Description',
    optional: '(optional)',
    contextTargetConstraints: 'Context, target, constraints…',
    cancel: 'Cancel',
    creating: 'Creating…',
    createTheProject: 'Create the project',
    project: 'Project',
    aiLaunchPlan: 'AI launch plan',
    status: 'Status',
    stage: 'Stage',
    progress: 'Progress',
    noProjects: 'No projects.',
    couldNotDelete: 'Could not delete.',
    projects: 'Projects',
    yourLaunchCampaignsAndTheirCollaboration: 'Your launch campaigns and their collaboration.',
    newProject2: '+ New project',
    active: 'Active',
    withAiPlan: 'With AI plan',
    avgProgress: 'Avg. progress',
    cards: '▦ Cards',
    table: '☰ Table',
    tryAgain: 'Try again',
    noProjectYet: 'No project yet',
    createYourFirstLaunchProjectThe: 'Create your first launch project: the AI will propose a structured plan from strategy to growth.',
    createAProject: '+ Create a project',
    deletePermanently: 'Delete permanently',
    permanentlyDeleteTheProjectNameThis: 'Permanently delete the project “{{name}}”? This cannot be undone: the project and its associated data (members, resources, checklist) are removed from the database (net-zero cleanup).',
    deleting: 'Deleting…',
    stagePosition: 'stage position',
  },
};

export default en;
